import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import type { CookieConsentState } from '@/store/slices/cookie-consent.slice';
import { useIsAppInstallBannerGoneSelector } from '@/store/slices';
import { useGetProbabilityEventDetailsQuery } from '../store/api/probability-event.api';
import {
	hasOpenedResourcesGuide,
	hasSeenResourcesCoachmark,
	isResourcesGuideAnchorVisible,
	markResourcesCoachmarkSeen,
	markResourcesGuideOpened,
	resolveResourcesGuide,
} from '../utils/probability-event.utils';
import { ResourcesCoachmark, ResourcesGuideModal } from '../components/resources';

/** Give the header a moment to settle before pointing at it. */
const COACHMARK_START_DELAY_MS = 400;
const ANCHOR_RETRY_INTERVAL_MS = 100;
const ANCHOR_RETRY_LIMIT = 12;

interface ResourcesGuideContextValue {
	isEnabled: boolean;
	registerTrophyAnchor: (element: HTMLElement | null) => void;
	openGuide: () => void;
	showPulse: boolean;
}

const disabledResourcesGuideContext: ResourcesGuideContextValue = {
	isEnabled: false,
	registerTrophyAnchor: () => undefined,
	openGuide: () => undefined,
	showPulse: false,
};

const ResourcesGuideCtx = createContext<ResourcesGuideContextValue>(disabledResourcesGuideContext);

interface ResourcesGuideProviderProps {
	children: ReactNode;
	eventId: string | null;
	userId?: string | number | null;
	enabled: boolean;
	showCoachMarkOnLoad: boolean;
	hasBlockingPopup: boolean;
}

export function ResourcesGuideProvider({
	children,
	eventId,
	userId,
	enabled,
	showCoachMarkOnLoad,
	hasBlockingPopup,
}: ResourcesGuideProviderProps) {
	const isCookiePending = useSelector(
		(state: { cookieConsent: CookieConsentState }) => state.cookieConsent.value === null
	);
	const isInstallBannerGone = useIsAppInstallBannerGoneSelector();
	const {
		data: eventDetails,
		isUninitialized,
		isLoading,
		isFetching,
	} = useGetProbabilityEventDetailsQuery(enabled && eventId ? { eventId } : skipToken);
	const resolvedGuide = useMemo(() => resolveResourcesGuide(eventDetails), [eventDetails]);
	const isDetailsPending =
		Boolean(enabled && eventId) &&
		(isUninitialized || isLoading || (isFetching && eventDetails == null));
	const isGuideActive = Boolean(
		enabled && eventId && !isDetailsPending && resolvedGuide.hasConfiguredSections
	);
	// The tooltip is opt-in per event: without admin-written copy there is nothing to say.
	const isCoachmarkActive = isGuideActive && resolvedGuide.hasConfiguredCoachmark;
	// Nothing else may own the screen. This hint paints above every modal in the app, so
	// anything underneath loses its taps to the coach mark's backdrop — cookie consent
	// its Accept, the terms dialog its only way out. The install banner is the other
	// way round: it hides the very trophy being pointed at.
	const isScreenClear = isInstallBannerGone && !isCookiePending && !hasBlockingPopup;
	const anchorRef = useRef<HTMLElement | null>(null);
	const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
	const [showCoachmark, setShowCoachmark] = useState(false);
	const [guideOpen, setGuideOpen] = useState(false);
	const [showPulse, setShowPulse] = useState(false);
	const wasCoachmarkOnScreenRef = useRef(false);

	const syncPulseState = useCallback(() => {
		if (!isGuideActive || !eventId || isCookiePending) {
			setShowPulse(false);
			return;
		}

		const coachmarkSeen = hasSeenResourcesCoachmark(eventId, userId);
		const guideOpened = hasOpenedResourcesGuide(eventId, userId);
		setShowPulse(coachmarkSeen && !guideOpened);
	}, [isGuideActive, eventId, isCookiePending, userId]);

	const updateAnchorRect = useCallback(() => {
		if (!isResourcesGuideAnchorVisible(anchorRef.current)) {
			setAnchorRect(null);
			return;
		}
		setAnchorRect(anchorRef.current!.getBoundingClientRect());
	}, []);

	const registerTrophyAnchor = useCallback(
		(element: HTMLElement | null) => {
			if (element && !isResourcesGuideAnchorVisible(element)) {
				return;
			}

			anchorRef.current = element;
			if (element) {
				updateAnchorRect();
				return;
			}
			setAnchorRect(null);
		},
		[updateAnchorRect]
	);

	const openGuide = useCallback(() => {
		if (!isGuideActive || !eventId || isCookiePending) return;

		setShowCoachmark(false);
		setGuideOpen(true);
		// Covers opening the guide from the header without ever seeing the coach mark:
		// the lesson has landed either way, so the hint must not come back later.
		markResourcesCoachmarkSeen(eventId, userId);
		markResourcesGuideOpened(eventId, userId);
		setShowPulse(false);
	}, [isGuideActive, eventId, isCookiePending, userId]);

	const dismissCoachmark = useCallback(() => {
		setShowCoachmark(false);
	}, []);

	useEffect(() => {
		syncPulseState();
	}, [syncPulseState]);

	useEffect(() => {
		if (isGuideActive) return;
		setGuideOpen(false);
		setShowCoachmark(false);
	}, [isGuideActive]);

	// Arms only once the screen is clear, so it neither appears under the install banner
	// nor races the banner's async decision to show itself.
	useEffect(() => {
		if (!isCoachmarkActive || !eventId || !showCoachMarkOnLoad || !isScreenClear) return;
		if (hasSeenResourcesCoachmark(eventId, userId)) return;

		let attempts = 0;
		let timer = 0;

		const tryShowCoachmark = () => {
			if (isResourcesGuideAnchorVisible(anchorRef.current)) {
				updateAnchorRect();
				setShowCoachmark(true);
				return;
			}

			if (attempts < ANCHOR_RETRY_LIMIT) {
				attempts += 1;
				timer = window.setTimeout(tryShowCoachmark, ANCHOR_RETRY_INTERVAL_MS);
			}
		};

		timer = window.setTimeout(tryShowCoachmark, COACHMARK_START_DELAY_MS);

		return () => window.clearTimeout(timer);
	}, [isCoachmarkActive, eventId, isScreenClear, showCoachMarkOnLoad, updateAnchorRect, userId]);

	const coachmarkVisible = showCoachmark && isScreenClear && isCoachmarkActive;

	useEffect(() => {
		if (coachmarkVisible) wasCoachmarkOnScreenRef.current = true;
	}, [coachmarkVisible]);

	// Retire the one-time hint only when it is really over: dismissed, the guide opened,
	// or the anchor gone. Yielding the screen to a popup leaves `showCoachmark` set, so
	// the hint is not burned and returns once the popup closes.
	useEffect(() => {
		if (showCoachmark || !wasCoachmarkOnScreenRef.current || !eventId) return;

		wasCoachmarkOnScreenRef.current = false;
		markResourcesCoachmarkSeen(eventId, userId);
		syncPulseState();
	}, [showCoachmark, eventId, syncPulseState, userId]);

	useEffect(() => {
		if (!coachmarkVisible) return;

		updateAnchorRect();
		const handleLayoutChange = () => {
			if (!isResourcesGuideAnchorVisible(anchorRef.current)) {
				setShowCoachmark(false);
				return;
			}
			updateAnchorRect();
		};
		window.addEventListener('resize', handleLayoutChange);
		window.addEventListener('scroll', handleLayoutChange, true);

		return () => {
			window.removeEventListener('resize', handleLayoutChange);
			window.removeEventListener('scroll', handleLayoutChange, true);
		};
	}, [coachmarkVisible, updateAnchorRect]);

	const value = useMemo(
		() => ({
			isEnabled: isGuideActive,
			registerTrophyAnchor,
			openGuide,
			showPulse: isGuideActive && showPulse && !coachmarkVisible,
		}),
		[coachmarkVisible, isGuideActive, openGuide, registerTrophyAnchor, showPulse]
	);

	if (!enabled || !eventId) {
		return (
			<ResourcesGuideCtx.Provider value={disabledResourcesGuideContext}>
				{children}
			</ResourcesGuideCtx.Provider>
		);
	}

	return (
		<ResourcesGuideCtx.Provider value={value}>
			{children}
			{isGuideActive ? (
				<>
					{isCoachmarkActive ? (
						<ResourcesCoachmark
							open={coachmarkVisible}
							anchorRect={anchorRect}
							title={resolvedGuide.coachmarkTitle}
							body={resolvedGuide.coachmarkBody}
							onDismiss={dismissCoachmark}
							onAnchorClick={openGuide}
						/>
					) : null}
					<ResourcesGuideModal
						open={guideOpen}
						onOpenChange={setGuideOpen}
						title={resolvedGuide.title}
						intro={resolvedGuide.intro}
						items={resolvedGuide.items}
					/>
				</>
			) : null}
		</ResourcesGuideCtx.Provider>
	);
}

export function useResourcesGuide() {
	return useContext(ResourcesGuideCtx);
}
